import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, AlertCircle, Info, Trash2, RefreshCw } from 'lucide-react';

const AdminErrorLogs = () => {
  const [data, setData] = useState({ logs: [], summary: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => { fetchLogs(); }, [levelFilter, sourceFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (levelFilter !== 'all') params.level = levelFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      const response = await axios.get(`${API}/admin/error-logs`, { params });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = async () => {
    if (!confirm('Clear all error logs?')) return;
    try {
      const res = await axios.delete(`${API}/admin/error-logs`);
      toast.success(`Cleared ${res.data.deleted} logs`);
      fetchLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'ERROR': case 'CRITICAL': return <AlertCircle className="h-4 w-4" />;
      case 'WARNING': return <AlertTriangle className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getLevelStyle = (level) => {
    switch (level) {
      case 'CRITICAL': return 'bg-red-600/15 text-red-500 border-red-500/30';
      case 'ERROR': return 'bg-destructive/15 text-destructive border-destructive/30';
      case 'WARNING': return 'bg-amber-500/15 text-amber-500 border-amber-500/30';
      default: return 'bg-blue-500/15 text-blue-500 border-blue-500/30';
    }
  };

  const uniqueSources = [...new Set(data.summary.map(s => s.source))];

  return (
    <AdminLayout title="Error Logs">
      <div className="space-y-6" data-testid="admin-error-logs-page">
        <div className="flex items-center justify-between pb-6">
          <p className="text-muted-foreground">System errors, warnings, and service issues</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-logs-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button variant="destructive" size="sm" onClick={clearLogs} data-testid="clear-logs-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Clear All
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {data.summary.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.summary.slice(0, 4).map((s, i) => (
              <Card key={i} className="border-border bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">{s.source}</p>
                      <p className="text-2xl font-bold">{s.count}</p>
                    </div>
                    <Badge className={getLevelStyle(s.level)}>{s.level}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3">
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="w-40" data-testid="level-filter">
              <SelectValue placeholder="All Levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="CRITICAL">Critical</SelectItem>
              <SelectItem value="ERROR">Error</SelectItem>
              <SelectItem value="WARNING">Warning</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-56" data-testid="source-filter">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {uniqueSources.map(src => (
                <SelectItem key={src} value={src}>{src}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data.logs.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Error Logs</h3>
              <p className="text-muted-foreground">System is running cleanly</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                Logs ({data.total})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="error-logs-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-40">Timestamp</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-24">Level</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-44">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.logs.map((log) => (
                      <tr key={log.id} className="hover:bg-muted/30 transition-colors" data-testid={`error-log-row-${log.id}`}>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap font-mono">
                          {new Date(log.timestamp).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${getLevelStyle(log.level)}`}>
                            {getLevelIcon(log.level)}
                            {log.level}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium">{log.source}</span>
                          {log.func && (
                            <span className="text-xs text-muted-foreground block">{log.func}:{log.line}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm break-all max-w-xl">{log.message}</p>
                          {log.exception && (
                            <p className="text-xs text-destructive mt-1 font-mono break-all">{log.exception}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminErrorLogs;
